import withStore, { TPresentable, TLoadingAwarePresenter } from "../withStore";
import Application from "../../Application";
import { IDevice } from "../../model/device/Device";
import { IUserCredential } from "../../model/user/UserCredential";
import RemoveDeviceInteractor from "../../interactor/device/RemoveDeviceInteractor";
import GetDeviceInteractor from "../../interactor/device/GetDevicesInteractor";
import DevicePresentationModel, {
  TDevicePresentationModel
} from "./model/DevicePresentationModel";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";

export interface TDevicePresenter extends TLoadingAwarePresenter {
  devices: TDevicePresentationModel[];
  selectedDevice?: TDevicePresentationModel;
  modalVisible: boolean;
  username: string;
  password: string;
  loginButtonDisabled: boolean;
}

export interface IDevicePresenter extends TDevicePresenter, TPresentable {
  setDevices(devices: IDevice[]): void;
  showModal(device: TDevicePresentationModel): void;
  hideModal(): void;
  removeDevice(deviceId: number, userCredentials: IUserCredential): void;
  getDevices(userCredentials: IUserCredential): void;
  setUsername(username: string): void;
  setPassword(password: string): void;
  changeLoginButtonStatus(): void;
  clearFields(): void;
}

const defaultState: TDevicePresenter = {
  devices: [],
  modalVisible: false,
  username: "",
  password: "",
  loginButtonDisabled: true
};

const DevicePresenter = withStore<IDevicePresenter, TDevicePresenter>(
  ({ application, store, loader, translate }): IDevicePresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TDevicePresenter & TLoadingAwarePresenter>();

    const DeviceMapper = (devices: IDevice[]) => {
      return (devices || []).map((device) => {
        return DevicePresentationModel(device);
      });
    };

    const setDevices = (devices: IDevice[]) => {
      _store.update({
        devices: DeviceMapper(devices)
      });
    };

    const showModal = (device?: TDevicePresentationModel) => {
      _store.update({
        modalVisible: true,
        selectedDevice: device
      });
    };

    const hideModal = () => {
      clearFields();
      _store.update({
        modalVisible: false,
        selectedDevice: undefined
      });
    };

    const getDevices = async (userCredential: IUserCredential) => {
      try {
        let devices = await _application.container
          .resolve<GetDeviceInteractor>("getDevices")
          .execute(userCredential);
        _store.update({
          devices: DeviceMapper(devices)
        });
      } catch (e: any) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute(e.message);
      }
    };

    const removeDevice = async (deviceId: number) => {
      const { username, password } = _store.getState<IDevicePresenter>();
      const userCredential: IUserCredential = { username, password };

      loader.start("removeDeviceLoader");

      try {
        await _application.container
          .resolve<RemoveDeviceInteractor>("removeDevice")
          .execute(deviceId, userCredential);
        await getDevices(userCredential);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("device_management_page.remove_device_success_message")
          );

        hideModal();
        loader.stop("removeDeviceLoader");
        clearFields();
      } catch (e: any) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute(e.message);

        loader.stop("removeDeviceLoader");
      }
    };

    const setUsername = (username: string) => {
      _store.update({
        username
      });

      changeLoginButtonStatus();
    };

    const setPassword = (password: string) => {
      _store.update({
        password
      });

      changeLoginButtonStatus();
    };

    const changeLoginButtonStatus = () => {
      const { username, password } = _store.getState<TDevicePresenter>();

      _store.update({
        loginButtonDisabled: username && password ? false : true
      });
    };

    const clearFields = () => {
      setUsername("");
      setPassword("");
    };

    return {
      ...state,

      store: _store,
      loader,
      application: _application,
      translate,
      setDevices,
      showModal,
      hideModal,
      removeDevice,
      getDevices,
      setUsername,
      setPassword,
      changeLoginButtonStatus,
      clearFields
    };
  },
  defaultState
);

export default DevicePresenter;
